<template>
  <div :class="['alert', 'd-flex', 'align-items-center', 'gap-2', config.cls]"
    role="status" aria-live="polite" data-test="precheck-indicator">
    <span v-if="state === 'pending' && !timedOut"
      class="spinner-border spinner-border-sm" aria-hidden="true"></span>
    <i v-else :class="config.icon"></i>
    <span>{{ text }}</span>
    <button v-if="logAvailable" class="btn btn-sm btn-outline-dark ms-auto"
      @click="$emit('open-log', submissionId)">View sanity check</button>
    <button v-if="timedOut" class="btn btn-sm btn-outline-dark ms-auto"
      @click="retry">Check again</button>
  </div>
</template>

<script>
import SubmissionController from '@/controllers/SubmissionController';

const POLL_MS = 2000;
export const MAX_WAIT_MS = 480000;

const STATES = {
  pending:  { cls: 'alert-secondary', icon: 'bi bi-hourglass-split',          text: 'Sanity check in progress…' },
  passed:   { cls: 'alert-success',   icon: 'bi bi-check-circle-fill',         text: 'Sanity check passed.' },
  failed:   { cls: 'alert-danger',    icon: 'bi bi-x-circle-fill',             text: 'Sanity check failed.' },
  error:    { cls: 'alert-warning',   icon: 'bi bi-exclamation-triangle-fill',
              text: 'Sanity check could not run (system error). This is not your fault — try again later or contact the teacher.' },
  disabled: { cls: 'alert-light',     icon: 'bi bi-info-circle',               text: 'Sanity check is not enabled for this subtask.' },
};

export default {
  name: 'PrecheckIndicator',
  props: {
    submissionId: { type: Number, required: true },
    startedAt:    { type: Number, required: true },
  },
  emits: ['finished', 'open-log'],
  data() {
    return {
      state: 'pending',
      logAvailable: false,
      elapsedMs: 0,
      timedOut: false,
      timeoutId: null,
      alive: true,
      deadline: this.startedAt + MAX_WAIT_MS,
      effectiveStart: this.startedAt,
    };
  },
  computed: {
    config() {
      return STATES[this.state] || STATES.pending;
    },
    text() {
      if (this.state === 'pending' && this.timedOut) {
        return 'No result yet. Click "Check again" or reload the page later.';
      }
      if (this.state === 'pending') {
        const secs = Math.floor(this.elapsedMs / 1000);
        return `${this.config.text} (${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')})`;
      }
      return this.config.text;
    },
  },
  mounted() {
    this._tick();
  },
  beforeUnmount() {
    this.alive = false;
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  },
  methods: {
    async _tick() {
      if (!this.alive) return;
      if (Date.now() >= this.deadline) {
        this.timedOut = true;
        this.timeoutId = null;
        this.$emit('finished');
        return;
      }
      if (document.hidden) {
        this.timeoutId = setTimeout(() => this._tick(), POLL_MS);
        return;
      }
      this.elapsedMs = Date.now() - this.effectiveStart;
      try {
        const s = await SubmissionController.getPrecheckStatus(this.submissionId);
        if (!this.alive) return;
        // Re-check after the async wait: a slow request can run past the deadline
        if (Date.now() >= this.deadline) {
          this.timedOut = true;
          this.timeoutId = null;
          this.$emit('finished');
          return;
        }
        this.state = s.state;
        this.logAvailable = s.log_available;
        if (s.state !== 'pending') {
          this.timeoutId = null;
          this.$emit('finished');
          return;
        }
      } catch (e) {
        const status = e?.response?.status;
        if (status >= 400 && status < 500 && status !== 408 && status !== 429) {
          // Permanent client error (401/403/404…) — treat like a timeout so the
          // component reaches a final state and finished is emitted.
          this.timedOut = true;
          this.timeoutId = null;
          this.$emit('finished');
          return;
        }
        // transient error: try again on the next tick
      }
      if (!this.alive) return;
      this.timeoutId = setTimeout(() => this._tick(), POLL_MS);
    },
    retry() {
      this.deadline = Date.now() + MAX_WAIT_MS;
      this.effectiveStart = Date.now();
      this.timedOut = false;
      this.elapsedMs = 0;
      this._tick();
    },
  },
};
</script>
